// SourceCodeReviewPage.js
import React from 'react';
import styles from './SourceCodeReviewPage.module.css';

const SourceCodeReviewPage = () => {
  return (
    <div className={styles.sourceCodeReviewPage}>
      {/* Main Content Section */}
      <section className={styles.mainSection}>
        <div className={styles.container}>
          <div className={styles.contentGrid}>
            {/* Left Content */}
            <div className={styles.leftContent}>
              <h1 className={styles.pageTitle}>Source Code Review</h1>
              <p className={styles.pageDescription}>
                At Virtual IP, our Source Code Review service is designed to 
                meticulously analyze the security and integrity of your application's 
                codebase. We go beyond surface-level scans to uncover deep-rooted vulnerabilities and enhance your code quality, ensuring robust 
                protection from within.
              </p>
            </div>

            {/* Right Visual - placeholder for now */}
            <div className={styles.rightVisual}>
              <div className={styles.heroBackground}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Deep Security-Centric Code Analysis Section */}
      <section className={styles.analysisSection}>
        <div className={styles.container}>
          <div className={styles.sectionGrid}>
            <div className={styles.sectionContent}>
              <h2 className={styles.sectionTitle}>
                Deep Security-Centric Code Analysis
              </h2>
              <div className={styles.titleUnderline}></div>
              <p className={styles.sectionDescription}>
                Our cybersecurity experts manually inspect your source code for 
                vulnerabilities such as SQL injection, cross-site scripting (XSS), 
                insecure object references, and other critical flaws. This helps 
                eliminate potential attack vectors before your application reaches 
                production.
              </p>
            </div>
            <div className={styles.sectionVisual}>
              <div className={styles.codeAnalysisIllustration}>
                <div className={styles.isometricPlatforms}>
                  <div className={styles.platform1}>
                    <div className={styles.platformBase}></div>
                    <div className={styles.codeBlock}></div>
                    <div className={styles.personFigure1}></div>
                  </div>
                  <div className={styles.platform2}>
                    <div className={styles.platformBase}></div>
                    <div className={styles.serverTower}></div>
                    <div className={styles.securityShield}></div>
                  </div>
                  <div className={styles.platform3}>
                    <div className={styles.platformBase}></div>
                    <div className={styles.dataVisualization}></div>
                    <div className={styles.personFigure2}></div>
                  </div>
                  <div className={styles.platform4}>
                    <div className={styles.platformBase}></div>
                    <div className={styles.mobileDevice}></div>
                  </div>
                  <div className={styles.platform5}>
                    <div className={styles.platformBase}></div>
                    <div className={styles.securityElement}></div>
                    <div className={styles.personFigure3}></div>
                  </div>
                  <div className={styles.floatingElements}>
                    <div className={styles.cloudIcon}></div>
                    <div className={styles.lockIcon}></div>
                    <div className={styles.codeSymbol}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detection of High-Risk Weaknesses Section */}
      <section className={styles.detectionSection}>
        <div className={styles.container}>
          <div className={styles.sectionGrid}>
            <div className={styles.sectionVisual}>
              <div className={styles.riskDetectionIllustration}>
                <div className={styles.detectionBackground}>
                  <div className={styles.riskMeterContainer}>
                    <div className={styles.riskMeter}>
                      <div className={styles.meterArc}></div>
                      <div className={styles.meterPointer}></div>
                      <div className={styles.riskLabel}>RISK</div>
                      <div className={styles.riskLevels}>
                        <span className={styles.lowLabel}>LOW</span>
                        <span className={styles.mediumLabel}>MEDIUM</span>
                        <span className={styles.highLabel}>HIGH</span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.analystFigure}>
                    <div className={styles.personBody}></div>
                    <div className={styles.magnifyingTool}></div>
                  </div>
                  <div className={styles.backgroundElements}>
                    <div className={styles.chartIcon}></div>
                    <div className={styles.gearIcon1}></div>
                    <div className={styles.gearIcon2}></div>
                    <div className={styles.dollarIcon}></div>
                    <div className={styles.plantIcon}></div>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.sectionContent}>
              <h2 className={styles.sectionTitle}>
                Detection of High-Risk Weaknesses
              </h2>
              <div className={styles.titleUnderline}></div>
              <p className={styles.sectionDescription}>
                We specialize in uncovering logic flaws, insecure authentication, 
                outdated encryption algorithms, and hardcoded secrets that 
                attackers could exploit. Virtual IP ensures that no hidden backdoors 
                or weak entry points exist in your application's foundation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Code Optimization Section */}
      <section className={styles.optimizationSection}>
        <div className={styles.container}>
          <div className={styles.sectionGrid}>
            <div className={styles.sectionContent}>
              <h2 className={styles.sectionTitle}>
                Code Optimization & Best Practices
              </h2>
              <div className={styles.titleUnderline}></div>
              <p className={styles.sectionDescription}>
                Beyond security, Virtual IP evaluates the quality of your code—
                refactoring inefficiencies, removing redundant logic, and enforcing 
                clean, maintainable coding practices that improve long-term 
                stability and scalability.
              </p>
            </div>
            <div className={styles.sectionVisual}>
              <div className={styles.optimizationIllustration}>
                <div className={styles.optimizationCard}>
                  <div className={styles.cardBackground}></div>
                  <div className={styles.codeSymbols}>
                    <div className={styles.brackets}>&lt;/&gt;</div>
                  </div>
                  <div className={styles.gearElements}>
                    <div className={styles.gear1}></div>
                    <div className={styles.gear2}></div>
                    <div className={styles.gear3}></div>
                  </div>
                  <div className={styles.handPointer}></div>
                  <div className={styles.pieChart}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Standards Compliance Section */}
      <section className={styles.complianceSection}>
        <div className={styles.container}>
          <div className={styles.sectionGrid}>
            <div className={styles.sectionVisual}>
              <div className={styles.complianceIllustration}>
                <div className={styles.complianceClipboard}>
                  <div className={styles.clipboardBackground}></div>
                  <div className={styles.clipboardHeader}></div>
                  <div className={styles.complianceTitle}>COMPLIANCE</div>
                  <div className={styles.checklistItems}>
                    <div className={styles.checkItem}>
                      <div className={styles.checkbox}></div>
                      <div className={styles.checkLine}></div>
                    </div>
                    <div className={styles.checkItem}>
                      <div className={styles.checkbox}></div>
                      <div className={styles.checkLine}></div>
                    </div>
                    <div className={styles.checkItem}>
                      <div className={styles.checkbox}></div>
                      <div className={styles.checkLine}></div>
                    </div>
                    <div className={styles.checkItem}>
                      <div className={styles.checkbox}></div>
                      <div className={styles.checkLine}></div>
                    </div>
                  </div>
                  <div className={styles.complianceBadge}></div>
                </div>
              </div>
            </div>
            <div className={styles.sectionContent}>
              <h2 className={styles.sectionTitle}>
                Standards Compliance & OWASP Alignment
              </h2>
              <div className={styles.titleUnderline}></div>
              <p className={styles.sectionDescription}>
                Our code reviews align with leading security frameworks like the 
                OWASP Top 10 and help you meet regulatory requirements 
                relevant to your industry (e.g., PCI-DSS, HIPAA). We ensure your 
                application is not only secure but also compliant and audit-ready.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SourceCodeReviewPage;